import React, { useState, useEffect } from 'react';
import { supabase } from '../../data/supabaseClient';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { CustomButton10Co, CustomButton10Ca, CustomButton10 } from '../../Templates';
import Spinner from '../../common/Spinner';
import { 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaUsers, 
  FaMapMarkerAlt,
  FaBox,
  FaTruck
} from 'react-icons/fa';

const DeliveryWaitingStatus = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  packageData,
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [showNoShowDialog, setShowNoShowDialog] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);

  // Determine user roles for delivery
  const isPackageSender = currentUser?.id === delivery?.sender_id;
  const isCarrier = currentUser?.id === delivery?.carrier_id;

  // Check arrival status
  const carrierHere = !!delivery?.delivery_carrier_here_at;
  const recipientHere = !!delivery?.delivery_recipient_here_at;
  const bothPresent = carrierHere && recipientHere;
  const timerStarted = !!delivery?.delivery_timer_started_at;

  // Calculate remaining time
  useEffect(() => {
    if (!delivery?.delivery_timer_started_at) return;

    const updateTimer = () => {
      const startTime = new Date(delivery.delivery_timer_started_at);
      const totalDuration = (delivery.meeting_duration_minutes || 3) * 60 * 1000; // Convert to milliseconds
      const additionalTime = (delivery.delivery_total_wait_time_minutes || 0) * 60 * 1000;
      const elapsed = Date.now() - startTime.getTime();
      const remaining = Math.max(0, totalDuration + additionalTime - elapsed);
      
      setTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [delivery?.delivery_timer_started_at, delivery?.meeting_duration_minutes, delivery?.delivery_total_wait_time_minutes]);

  // Format time remaining
  const formatTimeRemaining = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get current location for location proof
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString()
            });
          },
          (error) => reject(error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  // Handle package delivery completion (carrier only)
  const handleDeliveryComplete = async () => {
    setLoading(true);
    
    try {
      // Get location proof if possible
      let locationProof = null;
      try {
        locationProof = await getCurrentLocation();
      } catch (error) {
        console.log('Could not get location for delivery completion:', error);
      }

      const updateData = {
        status: 'package_delivered',
        delivery_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (locationProof) {
        updateData.delivery_completion_location_proof = locationProof;
      }

      // Update delivery status
      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Create notification for recipient
      await createNotification(
        delivery.sender_id,
        'package_delivered',
        t('package_actions.package_delivered_notification'),
        delivery.id
      );

      // Add system message
      await addSystemMessage(
        t('package_actions.package_delivered_system')
      );

      addToast({
        type: 'success',
        title: t('package_actions.delivery_completed'),
        description: t('package_actions.awaiting_confirmation')
      });

    } catch (error) {
      console.error('Error completing delivery:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_completing_delivery')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delivery extension request
  const handleDeliveryExtensionRequest = async (additionalMinutes = 3) => {
    setLoading(true);
    
    try {
      const extensionData = {
        requested_by: currentUser.id,
        requested_at: new Date().toISOString(),
        additional_minutes: additionalMinutes,
        status: 'pending'
      };

      const currentExtensions = delivery.delivery_extension_requests || [];
      const updatedExtensions = [...currentExtensions, extensionData];

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          delivery_extension_requests: updatedExtensions,
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Get other user ID for notification
      const otherUserId = isCarrier ? delivery.sender_id : delivery.carrier_id;

      await createNotification(
        otherUserId,
        'delivery_extension_request',
        t('package_actions.delivery_extension_request_notification', { 
          role: userRole,
          minutes: additionalMinutes 
        }),
        delivery.id
      );

      await addSystemMessage(
        t('package_actions.delivery_extension_request_system', { 
          role: userRole,
          minutes: additionalMinutes 
        })
      );

      addToast({
        type: 'info',
        title: t('package_actions.extension_requested'),
        description: t('package_actions.waiting_for_approval')
      });

      setShowExtensionDialog(false);

    } catch (error) {
      console.error('Error requesting delivery extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_requesting_delivery_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delivery extension response
  const handleDeliveryExtensionResponse = async (approve, extensionRequest) => {
    setLoading(true);
    
    try {
      const updatedExtensions = (delivery.delivery_extension_requests || []).map(req => 
        req.requested_at === extensionRequest.requested_at 
          ? { ...req, status: approve ? 'approved' : 'denied', responded_at: new Date().toISOString() }
          : req
      );

      const updateData = {
        delivery_extension_requests: updatedExtensions,
        updated_at: new Date().toISOString()
      };

      if (approve) {
        updateData.delivery_total_wait_time_minutes = (delivery.delivery_total_wait_time_minutes || 0) + extensionRequest.additional_minutes;
        updateData.delivery_wait_extension_count = (delivery.delivery_wait_extension_count || 0) + 1;
      }

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update(updateData)
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      await createNotification(
        extensionRequest.requested_by,
        approve ? 'delivery_extension_approved' : 'delivery_extension_denied',
        approve ? 
          t('package_actions.delivery_extension_approved_notification', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.delivery_extension_denied_notification'),
        delivery.id
      );

      await addSystemMessage(
        approve ? 
          t('package_actions.delivery_extension_approved_system', { minutes: extensionRequest.additional_minutes }) :
          t('package_actions.delivery_extension_denied_system')
      );

      addToast({
        type: approve ? 'success' : 'info',
        title: approve ? t('package_actions.extension_approved') : t('package_actions.extension_denied'),
        description: approve ? 
          t('package_actions.extra_time_added') : 
          t('package_actions.extension_was_denied')
      });

    } catch (error) {
      console.error('Error responding to delivery extension:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_responding_delivery_extension')
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delivery no-show report
  const handleDeliveryNoShowReport = async () => {
    setLoading(true);
    
    try {
      // Get location proof if possible
      let locationProof = null;
      try {
        locationProof = await getCurrentLocation();
      } catch (error) {
        console.log('Could not get location for delivery no-show report:', error);
      }

      const noShowData = {
        reported_by: currentUser.id,
        reported_at: new Date().toISOString(),
        location_proof: locationProof,
        phase: 'delivery'
      };

      const { error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          delivery_no_show_report: noShowData,
          status: isCarrier ? 'delivery_recipient_no_show' : 'delivery_carrier_no_show',
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id);

      if (updateError) throw updateError;

      // Get other user ID for notification
      const otherUserId = isCarrier ? delivery.sender_id : delivery.carrier_id;

      await createNotification(
        otherUserId,
        'delivery_no_show_reported',
        t('package_actions.delivery_no_show_reported_notification', { 
          reporter: userRole 
        }),
        delivery.id
      );

      await addSystemMessage(
        t('package_actions.delivery_no_show_reported_system', { 
          reporter: userRole,
          missing: isCarrier ? t('package_actions.recipient') : t('package_actions.carrier')
        })
      );

      addToast({
        type: 'info',
        title: t('package_actions.no_show_reported'),
        description: t('package_actions.delivery_cancelled')
      });

      setShowNoShowDialog(false);

    } catch (error) {
      console.error('Error reporting delivery no-show:', error);
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: t('package_actions.error_reporting_delivery_no_show')
      });
    } finally {
      setLoading(false);
    }
  };

  // Get pending extension request
  const pendingExtension = (delivery.delivery_extension_requests || [])
    .find(req => req.status === 'pending');

  // Render timer display
  const renderTimer = () => {
    if (!delivery?.delivery_timer_started_at) return null;

    const isExpired = timeRemaining <= 0;
    
    return (
      <div className="PAC-timer-section">
        <div className={`PAC-timer-display ${isExpired ? 'expired' : ''}`} 
             style={{ color: isExpired ? '#EF4444' : 'var(--accent-color2)' }}>
          {formatTimeRemaining(timeRemaining)}
        </div>
        <div className="PAC-timer-status">
          {isExpired ? 
            t('package_actions.delivery_time_expired') : 
            t('package_actions.delivery_time_remaining')
          }
        </div>
      </div>
    );
  };

  // Render presence indicators for delivery
  const renderDeliveryPresenceIndicators = () => (
    <div className="PAC-presence-indicators">
      <div className={`PAC-presence-indicator ${carrierHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {carrierHere ? <FaCheckCircle /> : <FaExclamationTriangle />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.carrier')}
        </div>
      </div>
      
      <div className={`PAC-presence-indicator ${recipientHere ? 'present' : ''}`}>
        <div className="PAC-presence-icon">
          {recipientHere ? <FaCheckCircle /> : <FaExclamationTriangle />}
        </div>
        <div className="PAC-presence-label">
          {t('package_actions.recipient')}
        </div>
      </div>
    </div>
  );

  // Handle different status scenarios
  if (delivery?.status === 'delivery_both_present') {
    return (
      <div className="PAC-meeting-section">
        <div className="PAC-meeting-title">
          <FaUsers size={14} />
          {t('package_actions.ready_for_delivery')}
        </div>
        
        {renderDeliveryPresenceIndicators()}
        
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <div style={{ fontSize: '11px', marginBottom: '8px', color: 'var(--accent-color)' }}>
            {t('package_actions.both_parties_confirmed_delivery')}
          </div>
          
          {isCarrier ? (
            <CustomButton10Co
              onClick={handleDeliveryComplete}
              label={t('package_actions.confirm_delivery')}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : <FaBox />}
            </CustomButton10Co>
          ) : (
            <div style={{ fontSize: '10px', opacity: 0.8 }}>
              {t('package_actions.waiting_carrier_delivery')}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="PAC-meeting-section">
      <div className="PAC-meeting-title">
        <FaTruck size={14} />
        {t('package_actions.delivery_waiting')}
      </div>
      
      {renderTimer()}
      {renderDeliveryPresenceIndicators()}
      
      {/* Extension request handling */}
      {pendingExtension && pendingExtension.requested_by !== currentUser?.id && (
        <div className="PAC-extension-section">
          <div className="PAC-extension-title">
            {t('package_actions.delivery_extension_request')}
          </div>
          <div className="PAC-extension-message">
            {t('package_actions.delivery_extension_request_message', { 
              role: isCarrier ? t('package_actions.recipient') : t('package_actions.carrier'),
              minutes: pendingExtension.additional_minutes 
            })}
          </div>
          <div className="PAC-action-buttons-v2">
            <CustomButton10Co
              onClick={() => handleDeliveryExtensionResponse(true, pendingExtension)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.approve')}
            </CustomButton10Co>
            <CustomButton10Ca
              onClick={() => handleDeliveryExtensionResponse(false, pendingExtension)}
              disabled={loading}
            >
              {loading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.deny')}
            </CustomButton10Ca>
          </div>
        </div>
      )}
      
      {/* Action buttons - only show for users who have confirmed arrival AND timer has started */}
      {!pendingExtension && timerStarted && (isCarrier ? carrierHere : recipientHere) && (
        <div className="PAC-action-buttons-v2">
          <CustomButton10
            onClick={() => setShowExtensionDialog(true)}
            label={t('package_actions.request_extension')}
            disabled={loading}
          />
          
          {timeRemaining <= 0 && (
            <CustomButton10Ca
              onClick={() => setShowNoShowDialog(true)}
              label={t('package_actions.report_no_show')}
              disabled={loading}
            />
          )}
        </div>
      )}
      
      {/* Show extension request to users who haven't arrived but timer is running */}
      {!pendingExtension && timerStarted && !(isCarrier ? carrierHere : recipientHere) && (
        <div className="PAC-action-buttons-v2">
          <CustomButton10
            onClick={() => setShowExtensionDialog(true)}
            label={t('package_actions.request_extension')}
            disabled={loading}
          />
        </div>
      )}
      
      {/* Message for users when timer hasn't started yet */}
      {!pendingExtension && !timerStarted && (
        <div style={{ textAlign: 'center', fontSize: '11px', opacity: 0.8, marginTop: '8px' }}>
          {t('package_actions.waiting_for_timer_start')}
        </div>
      )}
      
      {/* Extension request dialog */}
      {showExtensionDialog && (
        <div className="PAC-modal-overlay-v2">
          <div className="PAC-modal2">
            <h3>{t('package_actions.request_delivery_extension')}</h3>
            <p>{t('package_actions.delivery_extension_dialog_message')}</p>
            <div className="PAC-modal-buttons">
              <CustomButton10Ca
                onClick={() => setShowExtensionDialog(false)}
                label={t('buttons.cancel')}
                disabled={loading}
              />
              <CustomButton10Co
                onClick={() => handleDeliveryExtensionRequest(3)}
                label={t('package_actions.request_3_minutes')}
                disabled={loading}
              >
                {loading && <Spinner size="small" showText={false} thickness={2} />}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      )}
      
      {/* No-show report dialog */}
      {showNoShowDialog && (
        <div className="PAC-modal-overlay-v2">
          <div className="PAC-modal2">
            <h3>{t('package_actions.report_delivery_no_show')}</h3>
            <p>{t('package_actions.delivery_no_show_dialog_message', { 
              role: isCarrier ? t('package_actions.recipient') : t('package_actions.carrier')
            })}</p>
            <div className="PAC-modal-buttons">
              <CustomButton10Ca
                onClick={() => setShowNoShowDialog(false)}
                label={t('buttons.cancel')}
                disabled={loading}
              />
              <CustomButton10Co
                onClick={handleDeliveryNoShowReport}
                label={t('package_actions.confirm_no_show')}
                disabled={loading}
              >
                {loading && <Spinner size="small" showText={false} thickness={2} />}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryWaitingStatus;